export const common = {
  loadingProject: "Loading project...",
  loadingChapter: "Loading chapter...",
  loadingMessages: "Loading Messages...",
  export: "Export",
  exportAs: "Export as",
  exporting: "Exporting...",
  fileName: "File Name",
  cancel: "Cancel",
  submit: "Submit",
  addFiles: "Add files",
  clearChat: "Clear chat",
  notifications: {
    title: "Notifications",
    onlyShowUnread: "Only show unread",
    markAllAsRead: "Mark all as read",
    loading: "Loading...",
    empty: "No notifications yet",
    emptyUnread: "No unread notifications",
    unknownProject: "Unknown Project",
    unknownChapter: "Unknown Chapter",
    chapterReady: "Chapter Ready: {{chapter}}",
    chatResponded: "Chat responded: {{chapter}}",
    outlineReady: "Presentation: Outline ready",
    slidesReady: "Presentation: Slides ready",
    outlineOutdated: "Presentation: Outline is outdated",
    analysisReady: "Analysis ready: {{project}}",
    analysisReadyGeneric: "Analysis ready",
    analysisReadySubtitle: "Your project analysis is ready to view.",
    newNotification: "New notification",
    newNotificationSubtitle: "You have a new notification.",
    projectHasOne: 'Project "{{project}}" has {{count}} notification',
    projectHasMany: 'Project "{{project}}" has {{count}} notifications'
  },
  timeAgo: {
    justNow: "Just now",
    minutes: "{{count}}m ago",
    hours: "{{count}}h ago",
    days: "{{count}}d ago",
    weeks: "{{count}}w ago",
    months: "{{count}}mo ago"
  },
  status: {
    pass: "Pass",
    conditional: "Conditional",
    fail: "Fail",
    unvalidated: "Unvalidated",
    not_defined: "Undefined"
  },
  versions: {
    newTitle: "New Version",
    renameTitle: "Rename Version",
    deleteTitle: "Delete Version",
    nameLabel: "Version Name",
    namePlaceholder: "New version...",
    baseOn: "Base On",
    scope: "Scope",
    frameOnly: "Frame Only",
    frameOnlyDescription: "Just Project Setup and Case Framing, no analysis.",
    fullVersion: "Full Version",
    fullVersionDescription: "Everything, including analysis and results.",
    create: "Create Version",
    save: "Save",
    confirm: "Confirm",
    close: "Close",
    untitledProject: "Project",
    actions: "Version actions",
    rename: "Rename",
    delete: "Delete",
    deleteQuestion: "Delete {{version}} from {{project}}?",
    deleteQuestionNoProject: "Delete {{version}}?",
    deleteBody: "All version data and settings will be permanently deleted.",
    deletedToast: "Version {{name}} deleted.",
    createdToast: "Version {{name}} created.",
    renamedToast: "Version renamed to {{name}}.",
    undo: "Undo",
    view: "View",
    errors: {
      noProject: "Could not determine project to duplicate",
      nameRequired: "Name required",
      renameFailed: "Failed to rename",
      restoreFailed: "Failed to restore version",
      deleteFailed: "Failed to delete version",
      duplicateFailed: "Failed to duplicate"
    }
  },
  labsMode: {
    title: "Labs Mode",
    description: "Enable experimental features and early access functionality",
    menuDescription: "Labs Mode gives you access to experimental features that are currently being tested",
    enableFailed: "Failed to enable labs mode",
    disableFailed: "Failed to disable labs mode"
  },
  userMenu: {
    profile: "Profile",
    organization: "Organization",
    privacyNotice: "Privacy notice",
    termsOfUse: "Terms of Use",
    cookieNotice: "Cookie Notice",
    signOut: "Sign out",
    signOutError: "Failed to sign out. Please try again."
  }
} as const;

export const projectHome = {
  sidebar: {
    home: "Home",
    executiveSummary: "Executive Summary",
    innoveraMemo: "Innovera Memo",
    deepDives: "Deep Dives",
    marketAnalysis: "Market Analysis",
    strategyAnalysis: "Strategy Analysis",
    financialAnalysis: "Financial Analysis",
    appendices: "Appendices",
    hiddenChapters: "Hidden Chapters",
    context: "Context",
    frame: "Frame",
    projectSetup: "Project Setup",
    caseFrame: "Case Frame",
    valueChain: "Value Chain",
    vault: "Vault",
    artifacts: "Artifacts",
    exports: "Exports",
    collapse: "Collapse",
    expand: "Expand",
    collapseSidebar: "Collapse sidebar",
    expandSidebar: "Expand sidebar"
  },
  valueChain: {
    readOnly: "Read-only",
    title: "Value chain",
    generate: "Generate",
    regenerate: "Regenerate",
    generating: "Generating...",
    generatedToast: "Value chain generated",
    regeneratedToast: "Value chain regenerated",
    generationFailed: "Generation failed. Please try again.",
    loadErrorTitle: "Couldn't load the value chain",
    loadErrorBody: "Something went wrong while fetching it. Check your connection and try again.",
    retry: "Retry",
    emptyTitle: "No value chain yet",
    emptyBodyEditor: "Generate the value chain from this project's analysis, vault documents and overview.",
    emptyBodyViewer: "A project editor can generate it from this project's analysis, vault documents and overview.",
    footerNote:
      "Generated from the project's analysis and vault; annotations quote the analysis above. Regenerate to reflect the latest content.",
    upstream: "Upstream",
    downstream: "Downstream",
    scrollEarlier: "Scroll to earlier stages",
    scrollLater: "Scroll to later stages",
    aboutPosition: "About {{label}}",
    whoHoldsTitle: "Who holds each stage, and what characterizes it?",
    colStage: "Stage",
    colWhoHolds: "Who holds it today",
    colCharacterizes: "What characterizes it",
    annotation: {
      concentration: "Concentration",
      rivalry: "Rivalry",
      inputDifficulty: "Hard to source",
      valueCapture: "Value capture"
    },
    position: {
      base: "Base Case",
      candidate: "Option",
      recommended: "Recommended",
      rejected: "Rejected"
    },
    owner: {
      ours: "Us, today",
      suppliers: "Our Suppliers",
      customers: "Our Customers",
      others: "Third Parties",
      nobody: "Unclaimed"
    },
    errors: {
      notEnabled: "Value chain is not enabled for this organization",
      inProgress: "Value chain generation is already in progress",
      noContent: "The project has no analysis or case brief yet — generate project content first",
      generationFailed: "Generation failed. Please try again."
    }
  },
  chapterHeader: {
    aq: "AQ",
    aqTitle: "Analysis Questions",
    iq: "IQ",
    iqTitle: "Intelligence Questions",
    claims: "Claims",
    claimsTitle: "Claims Extraction",
    comments: "Comments",
    commentsTitle: "Comments",
    vault: "Vault",
    vaultTitle: "Document Vault",
    fullWidth: "Full width",
    defaultWidth: "Default width",
    moreOptions: "More options",
    markdown: "Markdown",
    copy: "Copy",
    paste: "Paste",
    noContentToCopy: "No content to copy",
    markdownCopied: "Markdown copied to clipboard",
    copyFailed: "Failed to copy markdown",
    noContentInClipboard: "No content in clipboard",
    editorNotReady: "Editor not ready",
    markdownPasted: "Markdown pasted successfully",
    pasteFailed: "Failed to paste markdown"
  },
  header: {
    document: "Document",
    presentation: "Presentation",
    presentationDisabledTooltip: "Presentation will be available once the Document generation is complete.",
    exportProject: "Export Project",
    exportPresentation: "Export Presentation",
    duplicateProject: "Duplicate Project",
    duplicateConfirm: "Are you sure you want to duplicate this project?",
    duplicateAction: "Duplicate",
    duplicateFailed: "Failed to duplicate project",
    duplicateSuccess: "Project duplicated successfully",
    switchedTo: "Switched to {{name}}.",
    exportDocSuccess: "Document exported successfully",
    exportDocFailed: "Failed to export document",
    exportDownloadFailed: "Failed to download file",
    exportFormatUnavailable: "{{format}} is not available for this presentation"
  },
  overview: {
    decisionBrief: "Decision Brief",
    decisionBriefEmpty:
      "Decision overview is not available yet. It is generated from the Executive and Full Summary chapters.",
    opportunityFrame: "Opportunity Frame",
    successCriteria: "Success Criteria",
    keyInsights: "Key Insights",
    nextSteps: "Next Steps",
    target: "Target",
    gatePrefix: "Gate · {{criteria}}",
    outOfDate: "Out of date",
    outOfDateTooltip: "Out of date - refresh to update",
    refresh: "Refresh",
    refreshing: "Refreshing…",
    updated: "Updated {{date}}",
    failedToRefresh: "Failed to refresh",
    generating: "Generating overview",
    legendCount: "{{value}} {{status}}"
  },
  chat: {
    title: "Chat",
    askAnything: "Ask me anything...",
    chatDisabled: "Chat is disabled",
    clearChatConfirm: "Are you sure you want to clear the chat?"
  },
  caseFrame: {
    relaunch: "Relaunch Analysis",
    relaunching: "Relaunching…",
    relaunchDescription:
      "A new version will be created with your changes applied. You'll be redirected automatically and the analysis will restart. This may take 10–30 minutes.",
    relaunchConfirm: "Create & Relaunch",
    empty: "This project has no case frame yet.",
    relaunchFailed: "Failed to relaunch analysis",
    saveFailed: "Failed to save changes. Please try again.",
    instructionsSaveFailed: "Failed to save instructions. Please try again."
  },
  projectSetup: {
    visibility: "Visibility",
    organization: "Organization",
    private: "Private",
    language: "Language",
    version: "Version",
    versionInitial: "Initial",
    vault: "Vault",
    attachment: "Attachment",
    attachments: "Attachments",
    instructions: "Instructions",
    namePlaceholder: "Project Name",
    descriptionPlaceholder: "What would be a short description of this project?",
    dimensionAdd: "Add",
    dimensionAddValue: "Add value",
    dimensionSave: "Save",
    languageName: {
      english: "English",
      japanese: "Japanese"
    }
  },
  dashboard: {
    title: "Dashboard",
    chapter: "Chapter",
    taskInProgress: "Task In Progress",
    complete: "Complete",
    view: "View",
    documentsBeingAnalyzed: "Your documents are being analyzed",
    analyzeTimeEstimate: "It usually takes 1-3 minutes"
  },
  generation: {
    settingUpTitle: "Setting up your Analysis",
    settingUpDescription: "Getting things ready before the analysis begins.",
    settingUpEstimate: "This usually takes 1–3 minutes.",
    backToProjectList: "Back to Project List",
    queuedTitle: "{{title}} is Queued",
    queuedStartsAfter: "It starts as soon as these pages finish:",
    queuedStartsAfterOne: "It starts as soon as this page finishes:",
    queuedStartsShortly: "It will start shortly.",
    generatingChapter: "Generating chapter",
    progressStarted:
      "Analysis started and will take 10-30 minutes. You can safely navigate from this page and come back later.",
    progressOrder: "Chapters are written in dependency order, so they finish out of sequence.",
    openChapter: "Open {{name}}",
    cancelAnalysis: "Cancel Analysis",
    cancelAnalysisDescription:
      "This will stop all chapter generation and revert the project version to Draft. Generated chapters will be deleted.",
    keepGenerating: "Keep Generating",
    close: "Close"
  },
  exports: {
    empty: "Nothing exported yet",
    open: "Open",
    download: "Download",
    downloadFailed: "Failed to download file",
    deck: "Deck",
    memo: "Memo"
  },
  exportOptions: {
    close: "Close",
    type: "Type",
    fullReport: "Full Report",
    fullReportPages: "Every page, in full.",
    fullReportChapters: "Every chapter, in full.",
    summary: "Summary",
    summaryDescription: "Condensed overview only.",
    include: "Include",
    projectFrame: "Project Frame",
    projectInstructions: "Project Instructions",
    analysisQuestions: "Analysis Questions and Intelligence Questions",
    vaultFilesAndLinks: "Vault Files and Links"
  },
  vaultPage: {
    downloadAll: "Download All",
    downloading: "Downloading…",
    downloadAllFailed: "Failed to download files",
    empty: "No files in the vault yet",
    uploading: "Uploading: {{files}}",
    fullVersionChars: "{{size}} - chars (Full version)",
    processing: "Processing…",
    processFailed: "Failed to process",
    openLink: "Open link",
    download: "Download",
    downloadFailed: "Failed to download file",
    retryProcessing: "Retry processing",
    delete: "Delete",
    deleteTitle: "Delete Document",
    deleteDescription: "Are you sure you want to delete this document?",
    add: "Add",
    files: "Files",
    waitForUploads: "Wait for current uploads to finish",
    uploadLimit: "Upload up to {{maxFiles}} files (max {{maxMb}}MB total)",
    webLink: "Web Link",
    addWebLink: "Add Web Link",
    invalidUrl: "Please enter a valid URL",
    duplicateLink: "This web link has already been added",
    webLinkAdded: "Web link added and processing started",
    webLinkFailed: "Failed to add web link"
  },
  recordChip: {
    high: "High",
    medium: "Medium",
    low: "Low",
    notNow: "Not now",
    no: "No",
    yes: "Yes",
    fail: "Fail",
    failed: "Failed",
    pass: "Pass",
    passed: "Passed",
    rejected: "Rejected",
    recommended: "Recommended",
    weak: "Weak",
    weakened: "Weakened",
    strong: "Strong",
    negative: "Negative",
    positive: "Positive",
    critical: "Critical",
    blocked: "Blocked",
    moderate: "Moderate",
    partial: "Partial",
    partially: "Partially",
    conditional: "Conditional",
    watch: "Watch",
    unclear: "Unclear",
    atRisk: "At risk",
    confirmed: "Confirmed",
    onTrack: "On track"
  },
  instructionsModal: {
    title: "Instructions",
    general: "GENERAL",
    projectInstructions: "Project Instructions",
    presentation: "PRESENTATION",
    outlineInstructions: "Outline Instructions",
    pages: "PAGES",
    pageInstructions: "Page Instructions",
    projectPlaceholder: "Write Project Instructions",
    outlinePlaceholder: "Customize how presentation slides are generated. Leave empty for default behavior.",
    pagePlaceholder: "Write instructions for {{name}}",
    thisPage: "this page",
    saving: "Saving...",
    saveChanges: "Save Changes",
    saveFailed: "Failed to save instructions",
    saved: "Instructions saved"
  },
  presentation: {
    backToOutline: "Back to Outline",
    openInGamma: "Open in Gamma",
    outlineTitle: "Presentation Outline",
    created: {
      justNow: "Created just now",
      minutes: "Created {{count}} min ago",
      hourOne: "Created {{count}} hour ago",
      hourMany: "Created {{count}} hours ago",
      dayOne: "Created {{count}} day ago",
      dayMany: "Created {{count}} days ago"
    },
    refreshed: {
      justNow: "Refreshed just now",
      minutes: "Refreshed {{count}} min ago",
      hourOne: "Refreshed {{count}} hour ago",
      hourMany: "Refreshed {{count}} hours ago",
      dayOne: "Refreshed {{count}} day ago",
      dayMany: "Refreshed {{count}} days ago"
    },
    updatingSlides: "Updating slides...",
    previewSlides: "Preview Slides",
    creating: "Creating...",
    refreshing: "Refreshing...",
    createSlides: "Create Slides",
    refreshSlides: "Refresh Slides",
    loadingOutline: "Loading presentation outline...",
    refreshingOutline: "Refreshing Outline",
    creatingOutline: "Creating Outline",
    analyzingLine1: "Analyzing your document and",
    analyzingLine2: "structuring the outline",
    startTitle: "Start Your Presentation",
    startLine1: "Create a slide outline from your document, then",
    startLine2: "convert it into a fully designed presentation",
    createOutline: "Create Outline",
    refreshOutline: "Refresh Outline",
    slides: "Slides",
    addSlide: "Add slide",
    newSlide: "New Slide",
    deleteSlide: "Delete slide",
    untitledSlide: "Untitled slide",
    noSlides: "No slides yet. Create an outline to get started",
    slideNumber: "Slide {{number}}",
    slideTitlePlaceholder: "Slide Title",
    keyPointsPlaceholder: "• Key point 1\n• Key point 2\n• Key point 3",
    bold: "Bold (Ctrl+B)",
    italic: "Italic (Ctrl+I)",
    bulletList: "Bullet List",
    numberedList: "Numbered List",
    loadingPresentation: "Loading presentation...",
    pdfLoadFailed: "Failed to load PDF",
    chatPlaceholder: "Ask me anything...",
    cancel: "Cancel",
    chatEmptyLine1: "Create an outline",
    chatEmptyLine2: "to start chatting with AI",
    outlineReady: "Your outline is ready! You can edit the outline directly, or just ask me to do it.",
    steps: {
      indexing: "Indexing documents",
      planningStructure: "Planning slide structure",
      generatingContent: "Generating slide content",
      readingChapters: "Reading the chapters",
      planningStoryline: "Planning the storyline",
      writingSlides: "Writing the slides"
    },
    status: {
      noOutlineTitle: "No Outline Yet",
      noOutlineDescription: "Generate an outline first",
      upToDateTitle: "Source & Instructions up to date",
      upToDateDescription: "Your outline reflects the latest source and instructions.",
      sourceChangedTitle: "Source Changed",
      sourceChangedDescription: "The source document was modified. Refresh to keep your outline in sync.",
      instructionsChangedTitle: "Instructions Changed",
      instructionsChangedDescription: "The outline instructions were modified. Refresh to keep your outline in sync.",
      bothChangedTitle: "Source & Instructions Changed",
      bothChangedDescription: "The source document and instructions were modified. Refresh to keep your outline in sync."
    },
    notifications: {
      refresh: "Refresh",
      refreshMessage: "Refresh the outline",
      keepCurrent: "Keep Current",
      keepCurrentMessage: "Keep the current outline",
      keptResponse:
        "Got it, keeping your current outline. You can refresh it anytime from the toolbar or simply ask me.",
      sourceChangedContent: "I detected changes in your source document. Here's what was updated:",
      instructionsChangedContent: "I detected changes in your outline instructions.",
      bothChangedContent: "I detected changes in your source document and outline instructions. Here's what was updated:",
      sourceModified: "Source document modified",
      instructionsUpdated: "Outline instructions updated",
      refreshQuestion: "Would you like me to refresh the outline to reflect these changes?"
    }
  }
} as const;


export const createProject = {
  chatTitle: "Chat",
  newProjectTitle: "New Project",
  intro:
    "I'll ask a few questions and build out your project as you answer. Every field is optional — the more you share, the sharper the analysis",
  languageQuestion:
    "I'll ask a few questions and build out your project as you answer. Every field is optional — the more you share, the sharper the analysis\n\nTo start, choose your preferred language:",
  autoLanguageMsg: "Your project will be in English.",
  decisionQuestion: "Now tell me about the decision you're working on.",
  uploadFiles: "Do you have any reports, data, or documents you'd like to share?",
  decisionFromDocuments: "From your documents, it looks like you're deciding: **{{decision}}** Is that right, or how would you put it?",
  setupCompleteFiles: "{{fileCount}} file(s) added.",
  setupCompleteLinks: "{{linkCount}} link(s) added.",
  setupCompleteFilesAndLinks: "{{fileCount}} file(s) and {{linkCount}} link(s) added.",
  setupComplete: "Setup complete! Now tell me about the decision you're working on.",
  projectSetupComplete:
    "Project Setup complete! Continue to the next step, Case Framing, to finish creating your project.",
  nextStep: "Next Step",
  navNextCaseFrame: "Next: Case Frame",
  navConfirmed: "Confirmed",
  confirmSetup: "Confirm",
  walkthroughFormat: "This looks like a **{{value}}** decision. Is that right?",
  walkthroughScope: "On scope, here's what I took from your documents: {{value}} Does that match?",
  walkthroughIntent: "And what's driving it: {{value}} Is that right?",
  walkthroughSectionFilled: "I've filled in {{section}} from your documents.",
  walkthroughWriting: "Writing {{section}}",
  walkthroughChecking: "Checking {{section}} against your answers",
  walkthroughSectionFilledAdjusted: "I've filled in {{section}} from your documents and adjusted it to match your earlier answers.",
  walkthroughGeneralCheck: "Anything in {{section}} that doesn't look right?",
  walkthroughLooksRight: "Looks right",
  walkthroughReady: "Your brief has enough to launch the analysis. Launch now, or keep going through the remaining questions?",
  walkthroughLaunchNow: "Launch now",
  walkthroughKeepGoing: "Keep going",
  walkthroughFinishing: "I'll finish the remaining sections from your documents first. This takes under a minute.",
  walkthroughFinishingStatus: "Finishing the brief",
  walkthroughDone: "Thanks, that covers your documents. You can launch the analysis now, or tell me anything else you'd like to add.",
  walkthroughDoneWithGaps:
    "Thanks, that's what I could take from your documents. {{gaps}} You can launch the analysis now, or tell me anything else you'd like to add.",
  walkthroughUnreadFiles: "I couldn't read {{files}}.",
  walkthroughUnwrittenSections: "I couldn't fill in {{sections}} from them.",
  walkthroughReadIncomplete: "I couldn't finish reading them, so some sections may still be empty.",
  walkthroughAnythingElse: "Anything you'd like to add or correct before we go deeper?",
  navBackProjectSetup: "Back: Project Setup",
  backAtProjectSetupPrefix: "You're back at Project Setup.",
  backAtProjectSetupAllSet:
    "You're back at Project Setup. Everything here is set — let me know if you'd like to make any changes.",
  backAtProjectSetupNoChanges: "Perfect, no changes it is. Let's continue to Case Framing.",
  navCaseFrameResume: "You're in Case Frame — ask me anything about your project, or refine the brief on the left.",
  filesGotIt: "Got it! You can add files or links anytime, right here in the chat.",
  vaultSuccess: "Your file has been successfully added to the Vault.",
  vaultLinkSuccess: "Your link has been successfully added to the Vault.",
  refineIntro: "Hi! I'm here to help you refine your project frame. What would you like to change?",
  versionDraftReady: "Your draft is ready. Make your changes and launch the analysis when you're ready.",
  essentialsCovered: "You've covered the essentials. That's enough to start — or add more to get deeper insights.",
  skipForNow: "Skip for now",
  upload: "Upload",
  webLink: "Web Link",
  skip: "Skip",
  save: "Save",
  confirmDecision: "Yes, that's right",
  otherDecision: "Other",
  confirmDecisionReply: "Yes, that's right.",
  launchAnalysis: "Launch Analysis",
  keepEditing: "Keep Editing",
  keepEditingPrompt: "Let's keep refining the case brief. Ask me the next question that would strengthen it the most.",
  failedUpload: "Failed to upload: {{name}}",
  draftUploadLimit: "Documents for one project can add up to 100 MB. Not uploaded: {{names}}",
  dimensionDropdown: "What {{name}} fits this project best?",
  dimensionText: "What's the {{name}} of this project?",
  dimensionConfirm: "Confirm",
  dimensionSkip: "Skip",
  send: "Send",
  composerPlaceholder: "Type a message to AI...",
  composerParkedPlaceholder: "Use the buttons above to continue...",
  titles: {
    projectSetup: "Project Setup",
    caseFrame: "Case Frame",
    newVersion: "New Version",
    reviewRelaunch: "Review & Relaunch"
  },
  header: {
    percentComplete: "{{value}}% Complete",
    readyToLaunch: "Ready to launch",
    launchNudge: "You've covered the essentials. Launch whenever you're ready, or keep going to sharpen the analysis.",
    launchNudgeDismiss: "Got it",
    back: "Back",
    resetProject: "Reset project",
    analysisSettings: "Analysis settings",
    setupIncompleteTitle: "Setup Incomplete",
    setupIncompleteDescription: "Add a project name to continue — the minimum we need to get started",
    setupAnswerInChatDescription: "Answer the question in the chat to continue",
    frameEmptyTitle: "Project Frame Empty",
    frameEmptyDescription: "Add a project name and language — the minimum we need to get started.",
    frameIncompleteTitle: "Project Frame Incomplete",
    frameIncompleteDescription: "The more context you share in chat, the more accurate your analysis will be.",
    frameCompleteTitle: "Project Frame Complete",
    frameCompleteDescription: "The project frame has everything we need to run a thorough and accurate analysis.",
    resetTitle: "Reset Project",
    resetDescription:
      "By resetting, all your project information will be cleared. You'll need to fill in all the everything again. Are you sure?",
    resetConfirm: "Reset",
    launchIncompleteTitle: "Incomplete Brief",
    launchIncompleteDescription:
      "By launching analysis with an incomplete brief, your results may be less accurate or miss key insights.",
    launchCompleteTitle: "One Last Thing",
    launchCompleteDescription:
      "Take a quick look at your brief before launching. It drives the quality of your results."
  },
  status: {
    analyzingMessage: "Analyzing your message...",
    readingDocuments: "Reading your documents...",
    searchingVault: "Searching uploaded documents...",
    searchingWeb: "Searching the web...",
    updatingBrief: "Updating case frame...",
    craftingResponse: "Crafting response..."
  },
  documentStatus: {
    reading: "Reading…",
    read: "Added to case brief",
    unreadable: "Couldn't read this file"
  },
  launch: {
    launched: "Analysis launched",
    failed: "Failed to launch analysis. Please try again."
  },
  instructionsSaveFailed: "Failed to save instructions. They are kept locally but not persisted.",
  chatError: "Failed to get response. Please try again.",
  chatErrorMessage: "Sorry, something went wrong. Please try again.",
  editor: {
    placeholder: "Start writing your project frame...",
    projectName: "Project Name",
    projectDescription: "Project description",
    undo: "Undo",
    redo: "Redo",
    bold: "Bold",
    italic: "Italic",
    underline: "Underline"
  }
} as const;

export const settings = {
  title: "Settings",
  groups: {
    account: "Account",
    organization: "Organization"
  },
  tabs: {
    profile: "Profile",
    preferences: "Preferences",
    general: "General",
    team: "Team"
  },
  common: {
    save: "Save changes",
    saving: "Saving...",
    cancel: "Cancel",
    show: "Show",
    hide: "Hide"
  },
  profile: {
    heading: "Profile",
    uploadPhoto: "Upload Photo",
    removePhoto: "Remove Photo",
    removing: "Removing...",
    photoHintSize: "For best results, upload a square image (400x400 px)",
    photoHintFormat: "JPG, PNG or WebP format, up to 5MB",
    firstName: "First Name",
    lastName: "Last Name",
    invalidFileType: "Invalid file type. Only JPG, PNG and WebP files are allowed.",
    fileTooLarge: "File size must not exceed 5MB.",
    photoRemoved: "Profile photo removed successfully",
    photoRemoveFailed: "Failed to remove photo. Please try again.",
    updated: "Profile updated successfully",
    updateFailed: "Failed to update profile. Please try again."
  },
  password: {
    heading: "Change Password",
    current: "Current Password",
    new: "New Password",
    confirm: "Confirm New Password",
    minLength: "At least 8 characters",
    hasNumber: "At least one number",
    hasCapital: "At least one capital letter",
    matches: "Passwords match",
    changed: "Password changed successfully.",
    changeFailed: "Failed to change password."
  },
  preferences: {
    heading: "Preferences",
    uiLanguage: "Interface language",
    uiLanguageHint:
      "The language of buttons, menus and labels. A project's own content follows the project's language.",
    saved: "Preferences saved",
    saveFailed: "Failed to save preferences"
  },
  languageName: {
    english: "English",
    japanese: "Japanese"
  },
  snippets: {
    heading: "Custom Snippets URL",
    description:
      "Configure a custom URL for fetching AI prompts. When set, this URL will be used instead of the default snippets service.",
    none: "No custom snippets URL configured. Using default service.",
    edit: "Edit",
    clear: "Clear",
    configure: "Configure URL",
    urlLabel: "Snippets URL",
    invalidUrl: "Please enter a valid URL starting with http:// or https://",
    save: "Save",
    updated: "Custom snippets URL updated successfully",
    updateFailed: "Failed to update custom snippets URL"
  },
  roles: {
    clientUser: "Member",
    clientAdmin: "Admin",
    systemAdmin: "System Admin"
  },
  accessModes: {
    edit: "Edit",
    read: "Read"
  },
  statuses: {
    active: "Active",
    pending: "Pending",
    inactive: "Inactive",
    expired: "Expired"
  },
  team: {
    you: "(You)",
    projectCount: "{{count}} projects",
    selectRow: "Select row for {{name}}",
    resendInvite: "Resend Invite",
    resending: "Resending...",
    cancelInvite: "Cancel invite",
    cancelling: "Cancelling...",
    manageAccess: "Manage Access",
    inviteCancelled: "Invitation canceled successfully",
    inviteCancelFailed: "Failed to cancel invitation",
    inviteResent: "Invitation resent successfully",
    inviteResendFailed: "Failed to resend invitation"
  },
  invite: {
    title: "Invite a member to this organization",
    memberRole: "Member Role",
    selectOption: "Select Option",
    accessMode: "Access Mode",
    selectAccessMode: "Select access mode",
    firstName: "First Name",
    firstNamePlaceholder: "Enter first name",
    lastName: "Last Name",
    lastNamePlaceholder: "Enter last name",
    email: "Email Address",
    emailPlaceholder: "Enter email address",
    send: "Send Invitation",
    sent: "Invitation sent successfully",
    sendFailed: "Failed to send invitation",
    invalidForm: "Invalid form data. Fix errors and try again.",
    roleRequired: "Role is required",
    firstNameRequired: "First name is required",
    firstNameTooShort: "First name must be at least 2 characters",
    lastNameRequired: "Last name is required",
    lastNameTooShort: "Last name must be at least 2 characters",
    emailRequired: "Email is required",
    emailInvalid: "Invalid email address"
  },
  manageAccess: {
    title: "Manage access for {{name}}",
    memberRole: "Member Role",
    selectRole: "Select role",
    accessMode: "Access Mode",
    selectAccessMode: "Select access mode",
    memberStatus: "Member Status",
    selectStatus: "Select status",
    saveRole: "Save Role",
    noChanges: "No changes applied.",
    updated: "User updated successfully",
    updateFailed: "Failed to update user. Please try again."
  },
  teamTable: {
    heading: "Team",
    searchPlaceholder: "Search Members",
    remove: "Remove",
    user: "User",
    role: "Role",
    projects: "Projects",
    accountStatus: "Account Status",
    dateAdded: "Date Added",
    actions: "Actions",
    selectAll: "Select all rows",
    previous: "Previous",
    next: "Next",
    usersSelected_one: "{{count}} User Selected",
    usersSelected_other: "{{count}} Users Selected",
    showingResults: "Showing {{shown}} of {{total}} results",
    fetchFailed: "Error fetching organization data"
  },
  general: {
    heading: "General",
    loading: "Loading organization data...",
    aiModel: "AI Model",
    uploadLogo: "Upload Logo",
    removeLogo: "Remove Logo",
    removing: "Removing...",
    logoHintSize: "For best results, upload a square image (400x400 px)",
    logoHintFormat: "JPG, PNG or WebP format, up to 5MB",
    orgName: "Organization Name",
    orgInstructions: "Organization Instructions",
    orgInstructionsPlaceholder: "Type your instructions here",
    invalidFileType: "Invalid file type. Only JPG, PNG and WebP files are allowed.",
    fileTooLarge: "File size must not exceed 5MB.",
    orgIdMissing: "Organization ID not found",
    logoRemoved: "Organization logo removed successfully",
    logoRemoveFailed: "Failed to remove logo. Please try again.",
    updated: "Organization updated successfully",
    updateFailed: "Failed to update organization. Please try again."
  }
} as const;

export const projects = {
  toolbar: {
    searchPlaceholder: "Search by name...",
    newProject: "New Project"
  },
  sidebar: {
    projects: "Projects"
  },
  card: {
    open: "Open",
    duplicate: "Duplicate",
    delete: "Delete",
    version: "Version",
    status: "Status",
    lastModified: "Last Modified",
    modifiedBy: "Modified By",
    createdBy: "Created by:",
    private: "Private",
    projectName: "Project Name",
    creator: "Creator",
    owner: "Owner",
    dateModified: "Date Modified"
  },
  sort: {
    default: "Sort",
    sortByHeading: "Sort by",
    orderHeading: "Order",
    orders: {
      azAsc: "A–Z",
      azDesc: "Z–A",
      newestFirst: "Newest First",
      oldestFirst: "Oldest First",
      completedFirst: "Completed First",
      draftFirst: "Draft First"
    }
  },
  filters: {
    heading: "Filter",
    more: "More",
    clearAll: "Clear all",
    notSet: "Not set",
    noValues: "No values yet"
  },
  dimensions: {
    heading: "Dimensions",
    newTitle: "New Dimension",
    editTitle: "Edit Dimension",
    nameLabel: "Name",
    namePlaceholder: "Dimension name...",
    typeLabel: "Type",
    optionsLabel: "Options",
    optionPlaceholder: "Add an option...",
    addOption: "Add",
    removeOption: "Remove {{option}}",
    create: "Create",
    save: "Save",
    editAction: "Edit",
    deleteAction: "Delete",
    dragToReorder: "Drag to reorder",
    confirmDelete: "Delete {{name}} ?",
    confirmDeleteBody: "Removes this field from all projects permanently.",
    confirmDeleteAction: "Confirm delete",
    cancelDeleteAction: "Cancel delete",
    emptyTitle: "No dimensions yet",
    emptyBody: "Add dimensions to track what matters to your projects.",
    subtitleText: "Text",
    subtitleDropdown_one: "Dropdown · {{count}} option",
    subtitleDropdown_other: "Dropdown · {{count}} options",
    types: {
      dropdown: "Dropdown",
      dropdownDescription: "Users pick from a list",
      text: "Text",
      textDescription: "Users type their own option"
    },
    confirmChangeTitle: "Confirm change",
    confirmChangeAction: "Confirm",
    affectedCount_one: "({{count}} project value will be cleared.)",
    affectedCount_other: "({{count}} project values will be cleared.)",
    toasts: {
      created: "Dimension created",
      updated: "Dimension updated",
      removed: "Dimension removed"
    },
    errors: {
      createFailed: "Failed to create",
      updateFailed: "Failed to update",
      deleteFailed: "Failed to delete",
      reorderFailed: "Failed to reorder"
    }
  },
  properties: {
    heading: "Properties",
    showAll: "Show all",
    hideAll: "Hide all",
    system: "System",
    alwaysVisible: "Always visible",
    dimensions: "Dimensions",
    noDimensions: "No dimensions",
    manageDimensions: "Manage Dimensions"
  },
  chat: {
    title: "Chat",
    send: "Send",
    placeholder: "Ask a question about your projects…",
    welcome: "Hi! Ask me anything about your projects — statuses, owners, recent changes.",
    closeAriaLabel: "Close chat",
    optionsMenuLabel: "Chat options",
    stop: "Stop",
    noResponseFallback: "Sorry, I couldn't generate a response.",
    genericErrorFallback: "Sorry, something went wrong. Please try again."
  },
  savedFilters: {
    heading: "Saved Filters",
    save: "Save",
    saveTitle: "Save filter",
    renameTitle: "Rename filter",
    nameLabel: "Name",
    namePlaceholder: "Filter name...",
    emptyTitle: "No saved filters yet",
    emptyBody: "Set up filters and save them to reuse in one click.",
    rename: "Rename",
    delete: "Delete",
    more: "More",
    created: "Saved filter created",
    renamed: "Saved filter renamed",
    deleted: "\"{{name}}\" deleted",
    createFailed: "Failed to save filter",
    renameFailed: "Failed to rename filter",
    deleteFailed: "Failed to delete filter"
  },
  status: {
    options: {
      framing: "Framing",
      generating: "Generating",
      complete: "Complete",
      failed: "Failed"
    },
    framingProgress: "Framing ({{percent}}%)",
    generatingProgress: "Generating ({{generated}}/{{total}})",
    completeProgress: "Complete ({{generated}}/{{total}})"
  },

  errors: {
    loadFailedTitle: "Could not load projects",
    loadFailedBody: "Something went wrong on our side. Try again in a moment.",
    retry: "Try again",
    duplicateFailed: "Failed to duplicate project",
    deleteDraftFailed: "Failed to delete draft",
    deleteProjectFailed: "Failed to delete project"
  },
  toasts: {
    projectDuplicated: "Project duplicated",
    draftDeleted: "Draft deleted",
    projectDeleted: "Project deleted"
  },
  deleteModal: {
    draftTitle: "Delete draft",
    projectTitle: "Delete project",
    draftDescription: "This action cannot be undone. The draft and all its data will be permanently deleted.",
    projectDescription:
      "This action cannot be undone. All project data, versions, and settings will be permanently deleted.",
    deleteAction: "Delete"
  }
} as const;
